import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { PageProps } from '@/lib/market/builders/shared'
import type { StockDetailData } from '@/lib/market/builders/stock'

import Link from 'next/link'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Panel, PanelBody, PanelHeader } from '@/components/generals/panel'
import { buildStockDetail } from '@/lib/market/builders/stock'
import { withMarket } from '@/lib/market/builders/shared'
import AnnouncementList from '@/components/market/announcement-list'
import CorporateActions from '@/components/market/corporate-actions'
import KeyData from '@/components/stock/key-data'
import OrderBookPanel from '@/components/stock/order-book'
import PeersTable from '@/components/stock/peers-table'
import PerformanceTable from '@/components/stock/performance-table'
import PositionCalculator from '@/components/stock/position-calculator'
import StockChart from '@/components/stock/stock-chart'
import StockFlows from '@/components/stock/stock-flows'
import StockHeader from '@/components/stock/stock-header'
import StockStatistics from '@/components/stock/stock-statistics'
import Technicals from '@/components/stock/technicals'
import Unavailable from '@/components/market/unavailable'

type StockPageProps = PageProps<StockDetailData>

export const getServerSideProps: GetServerSideProps<StockPageProps> = async context => {
    const slug = typeof context.params?.slug === 'string' ? context.params.slug : ''
    if (!slug) return { notFound: true }

    return withMarket(snapshot => buildStockDetail(snapshot, slug), { req: context.req })
}

const StockPage: FC<StockPageProps> = ({ data, error }): ReactNode => {
    if (!data) {
        return (
            <Layout>
                <Seo title='Company' />
                <Unavailable message={error} />
            </Layout>
        )
    }

    const { stock } = data

    return (
        <Layout key={stock.id}>
            <Seo
                title={`${stock.name} (${stock.ticker})`}
                description={`${stock.ticker} share price, interactive chart, order book, risk statistics, investor flows, dividends and sector peers on Kilele Exchange.`}
            />

            <StockHeader stock={stock} sessionDay={data.sessionDay} />

            <div className='grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]'>
                <div className='min-w-0 space-y-4'>
                    <Panel id='chart' labelledBy='chart-title'>
                        <PanelHeader id='chart-title' eyebrow='Price history' title={`${stock.ticker} chart`} description='Hover or use the arrow keys to read each session. Long ranges switch to weekly candles.' />
                        <PanelBody>
                            <StockChart ticker={stock.ticker} series={data.series} live={stock.live} benchmark={data.benchmark} />
                        </PanelBody>
                    </Panel>

                    <Panel id='statistics' labelledBy='statistics-title'>
                        <PanelHeader id='statistics-title' eyebrow='Statistics' title='Risk and return' description='Measured from daily closing prices against the Kilele All Share Index.' />
                        <PanelBody>
                            <StockStatistics ticker={stock.ticker} data={data} />
                        </PanelBody>
                    </Panel>

                    <div className='grid gap-4 lg:grid-cols-2'>
                        <Panel id='performance' labelledBy='performance-title'>
                            <PanelHeader id='performance-title' eyebrow='Relative performance' title='Against the sector and the market' />
                            <PanelBody>
                                <PerformanceTable
                                    ticker={stock.ticker}
                                    sectorName={stock.sectorName}
                                    sectorId={stock.sectorId}
                                    stock={data.relative.stock}
                                    sector={data.relative.sector}
                                    index={data.relative.index}
                                />
                            </PanelBody>
                        </Panel>

                        <Panel id='flows' labelledBy='flows-title'>
                            <PanelHeader
                                id='flows-title'
                                eyebrow='Flow'
                                title='Order and investor flow'
                                action={<Link href='/flows' className='text-xs font-medium text-muted-foreground hover:text-primary'>Market flows</Link>}
                            />
                            <PanelBody>
                                <StockFlows ticker={stock.ticker} live={stock.live} foreign={data.foreign} orderFlow={data.orderFlow} />
                            </PanelBody>
                        </Panel>
                    </div>

                    <Panel id='dividends' labelledBy='dividends-title'>
                        <PanelHeader id='dividends-title' eyebrow='Corporate actions' title='Dividends and entitlements' />
                        <PanelBody>
                            <CorporateActions items={data.actions} today={data.today} showCompany={false} />
                        </PanelBody>
                    </Panel>

                    <Panel id='peers' labelledBy='peers-title'>
                        <PanelHeader
                            id='peers-title'
                            eyebrow='Comparables'
                            title={stock.sectorName && stock.sectorId ? <>Peers in <span data-mdlfy-cms={`sectors:${stock.sectorId}:name`}>{stock.sectorName}</span></> : 'Sector peers'}
                        />
                        <PanelBody>
                            <PeersTable peers={data.peers} />
                        </PanelBody>
                    </Panel>
                </div>

                <aside className='min-w-0 space-y-4' aria-label={`${stock.ticker} details`}>
                    <Panel id='key-data' labelledBy='key-data-title'>
                        <PanelHeader id='key-data-title' title='Key data' />
                        <PanelBody>
                            <KeyData stock={stock} capRank={data.capRank} listedCount={data.listedCount} />
                        </PanelBody>
                    </Panel>

                    <Panel id='order-book' labelledBy='order-book-title'>
                        <PanelHeader id='order-book-title' title='Order book' description='Best eight levels on each side, refreshed during the session.' />
                        <PanelBody>
                            <OrderBookPanel
                                ticker={stock.ticker}
                                day={data.sessionDay}
                                live={stock.live}
                                averageVolume={stock.avgVolume30}
                                initialBook={data.book}
                                initialTrades={data.trades}
                            />
                        </PanelBody>
                    </Panel>

                    <Panel id='technicals' labelledBy='technicals-title'>
                        <PanelHeader id='technicals-title' title='Technical analysis' />
                        <PanelBody>
                            <Technicals series={data.series} live={stock.live} pivots={data.pivots} />
                        </PanelBody>
                    </Panel>

                    <Panel id='position' labelledBy='position-title'>
                        <PanelHeader id='position-title' title='Position calculator' />
                        <PanelBody>
                            <PositionCalculator ticker={stock.ticker} live={stock.live} dps={stock.dps} />
                        </PanelBody>
                    </Panel>

                    <Panel id='profile' labelledBy='profile-title'>
                        <PanelHeader id='profile-title' title='Company profile' />
                        <PanelBody className='space-y-4'>
                            {data.profile.description && (
                                <p className='whitespace-pre-line text-[13px] leading-relaxed text-foreground/90' data-mdlfy-cms={`companies:${stock.id}:description`}>
                                    {data.profile.description}
                                </p>
                            )}

                            <dl className='divide-y divide-border/70 text-[13px]'>
                                {data.profile.headquarters && (
                                    <div className='flex justify-between gap-4 py-2'>
                                        <dt className='text-muted-foreground'>Headquarters</dt>
                                        <dd className='text-right text-foreground' data-mdlfy-cms={`companies:${stock.id}:headquarters`}>{data.profile.headquarters}</dd>
                                    </div>
                                )}
                                {stock.sectorName && stock.sectorId && (
                                    <div className='flex justify-between gap-4 py-2'>
                                        <dt className='text-muted-foreground'>Sector</dt>
                                        <dd className='text-right text-foreground' data-mdlfy-cms={`sectors:${stock.sectorId}:name`}>{stock.sectorName}</dd>
                                    </div>
                                )}
                                {stock.segment && (
                                    <div className='flex justify-between gap-4 py-2'>
                                        <dt className='text-muted-foreground'>Market segment</dt>
                                        <dd className='text-right text-foreground' data-mdlfy-cms={`companies:${stock.id}:segment`}>{stock.segment}</dd>
                                    </div>
                                )}
                                <div className='flex justify-between gap-4 py-2'>
                                    <dt className='text-muted-foreground'>Ticker</dt>
                                    <dd className='font-mono text-foreground'>{stock.ticker}</dd>
                                </div>
                            </dl>
                        </PanelBody>
                    </Panel>

                    <Panel id='company-announcements' labelledBy='company-announcements-title'>
                        <PanelHeader
                            id='company-announcements-title'
                            title='Announcements'
                            action={<Link href='/announcements' className='text-xs font-medium text-muted-foreground hover:text-primary'>All</Link>}
                        />
                        <PanelBody>
                            <AnnouncementList items={data.announcements} compact emptyText='No announcements for this company yet.' />
                        </PanelBody>
                    </Panel>
                </aside>
            </div>
        </Layout>
    )
}

export default StockPage
